import { useLayoutEffect, useRef } from 'react';
import { isIos } from '../../helpers/deviceCheck/isIos';
/** Module level state to share across multiple modal / drawer instances */
let lockCount = 0;
let scrollY = 0;
const LOCKED_SCREEN_CSS_PROPERTIES = [
    'overflow',
    'padding-right',
    'width',
    'position',
    'top',
    'left',
    'right'
];
const originalStyles = {};
/** Helpers */
const scrollBarWidth = () => window.innerWidth - document.documentElement.clientWidth;
const saveStyles = (styles) => LOCKED_SCREEN_CSS_PROPERTIES.forEach((key) => {
    originalStyles[key] = styles.getPropertyValue(key);
});
const restoreStyles = (styles) => LOCKED_SCREEN_CSS_PROPERTIES.forEach((key) => {
    const value = originalStyles[key];
    if (value !== undefined && value !== null) {
        styles.setProperty(key, value);
    }
    else {
        styles.removeProperty(key);
    }
});
const lockBody = () => {
    const { style } = document.body;
    saveStyles(style);
    scrollY = window.scrollY;
    const gap = scrollBarWidth();
    /** Helps avoid layout shift due to bar disappearing when overflow of body is hidden mobile devices will generally have 0 scroll bar width */
    if (gap) {
        style.setProperty('padding-right', `${gap}px`);
    }
    style.setProperty('overflow', 'hidden');
    /**
     * For IOS overflow hidden is not enough for preventing scrolling behavior.
     * One work around for IOS is to have a fixed layout and making the top the current scrollY offset in order to remain in place
     * In the unlockBody function the scroll is restored. Has no impact in desktop.
     */
    if (isIos()) {
        style.setProperty('position', 'fixed');
        style.setProperty('top', `-${scrollY}px`);
        style.setProperty('left', '0');
        style.setProperty('right', '0');
    }
};
const unlockBody = () => {
    restoreStyles(document.body.style);
    window.scrollTo(0, scrollY);
};
/**
 * Locks background scrolling when both `enabled` and `active` options are `true`.
 * Automatically restores the original body scroll state when the component unmounts or `active`becomes false
 *
 * This hook supports both Desktop and IOS browsers which are more finnicky
 *
 * - On most browsers it applies `overflow: hidden` and preserves the scrollbar gap
 * - On IOS browsers it uses `position: fixed` and scroll offset compensation
 *
 * Scroll locking is reference-counted from the module instantiated variable `lockCount`
 * this allows for multiple calls to this hook to not interfere with another, (e.g. nested modals)
 * The initial state is restored only after the last active instance is removed
 *
 * This hook is heavily inspired by (the body-scroll-lock)[https://github.com/willmcpo/body-scroll-lock/blob/master/src/bodyScrollLock.js#L110] library
 * @param enabled boolean value to OPT IN to this hook, otherwise skips over
 * @param active boolean value to lock screen for a certain condition
 *
 * @example
 * useLockBodyScroll({ enabled: preventScroll, active: isOpen });
 */
export const useLockBodyScroll = ({ enabled = false, active = false }) => {
    const isLocked = useRef(false);
    useLayoutEffect(() => {
        /** Early return for OPT in `enabled` flag and SSR Guard */
        if (typeof window === 'undefined' || !enabled)
            return;
        /** Lock the page (only on first global lock) */
        const lock = () => {
            if (lockCount === 0) {
                lockBody();
            }
            lockCount += 1;
            isLocked.current = true;
        };
        /** Unlock the page (only when the last global lock is released) */
        const unlock = () => {
            lockCount = Math.max(0, lockCount - 1);
            if (lockCount === 0) {
                unlockBody();
            }
            isLocked.current = false;
        };
        if (active && !isLocked.current) {
            lock();
        }
        else if (!active && isLocked.current) {
            unlock();
        }
        // eslint-disable-next-line consistent-return
        return () => {
            if (isLocked.current) {
                unlock();
            }
        };
    }, [enabled, active]);
};
//# sourceMappingURL=useLockBodyScroll.js.map